import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  IRootState,
  IProductSyncRun,
  IProductSyncQueueItem,
  SyncRunStatus,
  SyncStepType,
} from '../../../types/store';
import {
  IGetSyncRunResponse,
  IGetRunQueueResponse,
  IGetSyncHistoryResponse,
} from '../../../types/api/response/productsresponse';
import { ProductsService } from '../../../api/services/productsService';
import { setProductSyncStatus } from '../../../store/Reducers/ProductsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { SyncStatusCard } from '../../../components/SyncStatusCard';

const STATUS_COLOR: Record<SyncRunStatus, 'warning' | 'success' | 'error' | 'default'> = {
  INPROGRESS: 'warning',
  FINISHED: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
};

const STEP_LABEL: Record<SyncStepType, string> = {
  CIN7_PULL: 'Cin7 Pull',
  CIN7_CLASSIFY: 'Classify',
  PRODUCT_QUEUE: 'Product Queue',
  UNKNOWN: 'Unknown',
};

const fmt = (dt: string | null | undefined) =>
  dt ? moment(dt).format('MMM D, YYYY HH:mm:ss') : '—';

const duration = (start: string, end: string | null) => {
  if (!end) return null;
  const secs = moment(end).diff(moment(start), 'seconds');
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

export const ProductSyncPage = () => {
  const dispatch = useDispatch();
  const syncStatus = useSelector((state: IRootState) => state.products.syncStatus);

  const [runs, setRuns] = useState<IProductSyncRun[]>([]);
  const [runsPage, setRunsPage] = useState(0);
  const [runsTotalElements, setRunsTotalElements] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);
  const [startingSync, setStartingSync] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState<IGetSyncRunResponse | null>(null);
  const [queueData, setQueueData] = useState<IGetRunQueueResponse | null>(null);
  const [queuePage, setQueuePage] = useState(0);
  const [queueTotalElements, setQueueTotalElements] = useState(0);
  const [queueStatusFilter, setQueueStatusFilter] = useState('');
  const [queueLoading, setQueueLoading] = useState(false);

  const [forceSyncSkus, setForceSyncSkus] = useState<string[]>([]);
  const [forcingSync, setForcingSync] = useState(false);

  const [historySkuInput, setHistorySkuInput] = useState('');
  const [historyData, setHistoryData] = useState<IGetSyncHistoryResponse | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchedHistorySku, setSearchedHistorySku] = useState('');

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await ProductsService.getLatestSyncStatus();
      dispatch(setProductSyncStatus(status));
      return status;
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load sync status. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
      return null;
    }
  }, [dispatch]);

  const loadRuns = useCallback(
    async (page = 0) => {
      setRunsLoading(true);
      try {
        const res = await ProductsService.getSyncRuns(page, 20);
        setRuns(res.content);
        setRunsTotalElements(res.page.totalElements);
      } catch (e: any) {
        dispatch(
          showNotification({
            message: `Failed to load sync runs. ${e?.response?.data?.message || ''}`,
            severity: 'error',
          }),
        );
      } finally {
        setRunsLoading(false);
      }
    },
    [dispatch],
  );

  const refresh = useCallback(async () => {
    const status = await loadSyncStatus();
    await loadRuns(runsPage);
    return status;
  }, [loadSyncStatus, loadRuns, runsPage]);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (syncStatus?.status === 'INPROGRESS') {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => {
          loadSyncStatus();
        }, 10000);
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadRuns(runsPage);
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncStatus?.status]);

  const handleStartSync = async () => {
    setStartingSync(true);
    try {
      const res = await ProductsService.syncLastModified();
      dispatch(showNotification({ message: res.message }));
      await refresh();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to start sync. ${e?.response?.data?.message || e}`,
          severity: 'error',
        }),
      );
    } finally {
      setStartingSync(false);
    }
  };

  const handleForceSync = async () => {
    const skus = forceSyncSkus.map((s) => s.trim()).filter(Boolean);
    if (skus.length === 0) return;

    setForcingSync(true);
    try {
      const res = await ProductsService.forceSync(skus);
      const parts: string[] = [];
      if (res.queued.length > 0) parts.push(`${res.queued.length} queued`);
      if (res.not_found.length > 0) parts.push(`${res.not_found.length} not found in Cin7`);
      if (res.failed.length > 0) parts.push(`${res.failed.length} failed`);
      dispatch(
        showNotification({
          message: `Force sync: ${parts.join(', ')}.`,
          severity: res.not_found.length > 0 || res.failed.length > 0 ? 'warning' : 'success',
        }),
      );
      setForceSyncSkus([]);
      await refresh();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to force sync. ${e?.response?.data?.message || e}`,
          severity: 'error',
        }),
      );
    } finally {
      setForcingSync(false);
    }
  };

  const loadHistory = async (sku: string, page: number) => {
    if (!sku) return;
    setHistoryLoading(true);
    try {
      const res = await ProductsService.getSyncHistory(sku, page, 20);
      setHistoryData(res);
      setHistoryTotalElements(res.page.totalElements);
      setSearchedHistorySku(sku);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load sync history. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSearchHistory = async () => {
    setHistoryPage(0);
    await loadHistory(historySkuInput.trim(), 0);
  };

  const handleViewRun = async (runId: number) => {
    setDialogOpen(true);
    setSelectedRun(null);
    setQueueData(null);
    setQueuePage(0);
    setQueueTotalElements(0);
    setQueueStatusFilter('');
    try {
      const details = await ProductsService.getSyncRun(runId);
      setSelectedRun(details);
      await loadQueue(runId, '', 0);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load run details. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const loadQueue = async (runId: number, statusFilter: string, page: number) => {
    setQueueLoading(true);
    try {
      const res = await ProductsService.getRunQueue(runId, statusFilter || undefined, page, 50);
      setQueueData(res);
      setQueueTotalElements(res.page.totalElements);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load queue items. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setQueueLoading(false);
    }
  };

  const handleQueueFilterChange = async (newStatus: string) => {
    setQueueStatusFilter(newStatus);
    setQueuePage(0);
    if (selectedRun?.run?.id) {
      await loadQueue(selectedRun.run.id, newStatus, 0);
    }
  };

  const runsColumns: GridColDef[] = [
    { field: 'id', headerName: 'Run ID', width: 90 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => (
        <Chip
          label={p.value}
          color={STATUS_COLOR[p.value as SyncRunStatus] ?? 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'startedAt',
      headerName: 'Started',
      width: 190,
      valueFormatter: (v: string) => fmt(v),
    },
    {
      field: 'completedAt',
      headerName: 'Completed',
      width: 190,
      valueFormatter: (v: string) => fmt(v),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 100,
      valueGetter: (_v: any, row: IProductSyncRun) =>
        duration(row.startedAt, row.completedAt) ?? '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => handleViewRun(p.row.id)}>
          View
        </Button>
      ),
    },
  ];

  const queueColumns: GridColDef[] = [
    { field: 'sku', headerName: 'SKU', width: 160 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => (
        <Chip
          label={p.value}
          color={
            p.value === 'COMPLETED'
              ? 'success'
              : p.value === 'FAILED'
                ? 'error'
                : p.value === 'CLAIMED'
                  ? 'warning'
                  : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'isDeletion',
      headerName: 'Deletion',
      width: 80,
      renderCell: (p) => (p.value ? '🗑' : ''),
    },
    {
      field: 'dbFieldsChanged',
      headerName: 'Product Details Changed',
      width: 175,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'attachmentsChanged',
      headerName: 'Images Changed',
      width: 130,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'indexableFieldsChanged',
      headerName: 'Index Fields Changed',
      width: 155,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'embeddingFieldsChanged',
      headerName: 'Embedding Fields Changed',
      width: 185,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'failedStep',
      headerName: 'Failed Step',
      width: 130,
      valueFormatter: (v: string) => v ?? '—',
    },
    {
      field: 'errorMessage',
      headerName: 'Error Message',
      width: 260,
      renderCell: (p) =>
        p.value ? (
          <Tooltip title={p.value}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.value}
            </span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'completedAt',
      headerName: 'Completed',
      width: 180,
      valueFormatter: (v: string) => fmt(v),
    },
  ];

  const historyColumns: GridColDef[] = [
    { field: 'sku', headerName: 'SKU', width: 160 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => (
        <Chip
          label={p.value}
          color={
            p.value === 'COMPLETED'
              ? 'success'
              : p.value === 'FAILED'
                ? 'error'
                : p.value === 'CLAIMED'
                  ? 'warning'
                  : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'isDeletion',
      headerName: 'Deletion',
      width: 80,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'dbFieldsChanged',
      headerName: 'Product Details Changed',
      width: 175,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'attachmentsChanged',
      headerName: 'Images Changed',
      width: 130,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'indexableFieldsChanged',
      headerName: 'Index Fields Changed',
      width: 155,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'embeddingFieldsChanged',
      headerName: 'Embedding Fields Changed',
      width: 185,
      renderCell: (p) => (p.value ? '✓' : ''),
    },
    {
      field: 'failedStep',
      headerName: 'Failed Step',
      width: 130,
      valueFormatter: (v: string) => v ?? '—',
    },
    {
      field: 'errorMessage',
      headerName: 'Error Message',
      width: 260,
      renderCell: (p) =>
        p.value ? (
          <Tooltip title={p.value}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.value}
            </span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Queued At',
      width: 180,
      valueFormatter: (v: string) => fmt(v),
    },
    {
      field: 'completedAt',
      headerName: 'Completed',
      width: 180,
      valueFormatter: (v: string) => fmt(v),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        <SyncStatusCard data={syncStatus} onRefresh={loadSyncStatus} />
        <Button
          variant="contained"
          onClick={handleStartSync}
          disabled={startingSync || syncStatus?.status === 'INPROGRESS'}
          sx={{ fontWeight: 600 }}
        >
          {startingSync && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
          {syncStatus?.status === 'INPROGRESS' ? 'Sync Running…' : 'Start Sync'}
        </Button>
      </Box>

      {/* Force sync specific SKUs */}
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Force Sync SKUs
      </Typography>
      <Box display="flex" alignItems="flex-start" gap={2} mb={3} flexWrap="wrap">
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={forceSyncSkus}
          onChange={(_e, newValue) => {
            const skus = (newValue as string[])
              .flatMap((v) => v.split(/[\s,]+/))
              .map((v) => v.trim())
              .filter(Boolean);
            setForceSyncSkus(Array.from(new Set(skus)));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="SKUs to force sync"
              placeholder="Type or paste SKUs, press Enter"
              size="small"
            />
          )}
          sx={{ minWidth: 400, flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleForceSync}
          disabled={forcingSync || forceSyncSkus.length === 0}
          sx={{ fontWeight: 600, height: 40 }}
        >
          {forcingSync && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
          Force Sync
        </Button>
      </Box>

      {/* Search sync history for a SKU */}
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Search Sync History
      </Typography>
      <Box display="flex" alignItems="flex-start" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="SKU"
          size="small"
          value={historySkuInput}
          onChange={(e) => setHistorySkuInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearchHistory();
          }}
          sx={{ minWidth: 250 }}
        />
        <Button
          variant="outlined"
          onClick={handleSearchHistory}
          disabled={historyLoading || !historySkuInput.trim()}
          sx={{ height: 40 }}
        >
          Search
        </Button>
      </Box>
      {historyData && (
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Sync history matching &quot;{searchedHistorySku}&quot; ({historyTotalElements} total)
          </Typography>
          <DataGrid
            rows={historyData.content}
            columns={historyColumns}
            getRowId={(row: IProductSyncQueueItem) => row.id}
            loading={historyLoading}
            autoHeight
            pageSizeOptions={[20]}
            paginationMode="server"
            rowCount={historyTotalElements}
            paginationModel={{ page: historyPage, pageSize: 20 }}
            onPaginationModelChange={(m) => {
              setHistoryPage(m.page);
              loadHistory(searchedHistorySku, m.page);
            }}
            sx={{ fontSize: 12 }}
          />
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Runs table */}
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Sync Runs
      </Typography>
      <DataGrid
        rows={runs}
        columns={runsColumns}
        getRowId={(row) => row.id}
        loading={runsLoading}
        autoHeight
        pageSizeOptions={[20]}
        paginationMode="server"
        rowCount={runsTotalElements}
        paginationModel={{ page: runsPage, pageSize: 20 }}
        onPaginationModelChange={(m) => {
          setRunsPage(m.page);
          loadRuns(m.page);
        }}
        sx={{ fontSize: 13 }}
      />

      {/* Run details dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        slotProps={{ paper: { sx: { height: '85vh' } } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={600}>Run #{selectedRun?.run?.id}</Typography>
            {selectedRun?.run?.status && (
              <Chip
                label={selectedRun.run.status}
                color={STATUS_COLOR[selectedRun.run.status] ?? 'default'}
                size="small"
              />
            )}
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!selectedRun ? (
            <Box display="flex" justifyContent="center" pt={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Run metadata */}
              <Box display="flex" gap={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">{fmt(selectedRun.run.startedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">{fmt(selectedRun.run.completedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body2">
                    {duration(selectedRun.run.startedAt, selectedRun.run.completedAt) ?? '—'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Steps */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Steps
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {selectedRun.run.steps?.map((step) => (
                    <Box
                      key={step.id}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      p={1.5}
                      bgcolor="#f9f9f9"
                      borderRadius={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="body2" fontWeight={600} width={130}>
                        {STEP_LABEL[step.syncType] ?? step.syncType}
                      </Typography>
                      <Chip
                        label={step.status}
                        color={STATUS_COLOR[step.status] ?? 'default'}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary" width={80}>
                        {duration(step.startedAt, step.completedAt) ?? '—'}
                      </Typography>
                      {step.productsTargeted != null && (
                        <Typography variant="body2" color="text.secondary">
                          {step.productsTargeted} targeted
                        </Typography>
                      )}
                      {step.productsSucceeded != null && (
                        <Typography variant="body2" color="success.main">
                          {step.productsSucceeded} succeeded
                        </Typography>
                      )}
                      {step.productsFailed != null && step.productsFailed > 0 && (
                        <Typography variant="body2" color="error.main">
                          {step.productsFailed} failed
                        </Typography>
                      )}
                      {step.message && (
                        <Tooltip title={step.message}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {step.message}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Divider />

              {/* Queue items */}
              <Box flex={1} display="flex" flexDirection="column">
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Queue Items
                    {queueData && (
                      <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                        ({queueData.page?.totalElements ?? queueData.content.length} total)
                      </Typography>
                    )}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={queueStatusFilter}
                      label="Status"
                      onChange={(e) => handleQueueFilterChange(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="CLAIMED">Claimed</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="FAILED">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {queueLoading ? (
                  <Box display="flex" justifyContent="center" pt={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <DataGrid
                    rows={queueData?.content ?? []}
                    columns={queueColumns}
                    getRowId={(row: IProductSyncQueueItem) => row.id}
                    autoHeight
                    pageSizeOptions={[50]}
                    paginationMode="server"
                    rowCount={queueTotalElements}
                    paginationModel={{ page: queuePage, pageSize: 50 }}
                    onPaginationModelChange={(m) => {
                      setQueuePage(m.page);
                      if (selectedRun?.run?.id) {
                        loadQueue(selectedRun.run.id, queueStatusFilter, m.page);
                      }
                    }}
                    sx={{ fontSize: 12 }}
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
